import { cn } from "@/lib/utils/cn";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

/**
 * Encode every visual deviation as a prop (`variant`, `size`, `font`) — never
 * as a per-site `className` override. If a caller needs a color/style the
 * props don't cover, add a new variant here:
 *   1. Prefer semantic names (`success`, `warning`, `destructive`) when the
 *      meaning is shared across the app.
 *   2. Fall back to a color descriptor (`amber`, `foreground`, `blue-soft`)
 *      only when the usage is bespoke and has no shared semantic.
 *
 * `className` is reserved for genuine one-offs: layout margins (`ml-2`),
 * parent-group interactive state (`group-hover/x:...`), and the rare escape
 * hatch. Color, typography, padding, and height should always be attributes.
 */
const badgeVariants = cva(
  "inline-flex items-center justify-center rounded-md border font-semibold w-fit whitespace-nowrap shrink-0 gap-1 ring-ring/10 dark:ring-ring/20 dark:outline-ring/40 outline-ring/50 focus-visible:ring-4 focus-visible:outline-1 aria-invalid:focus-visible:ring-0 transition-[color,box-shadow]",
  {
    variants: {
      variant: {
        // Semantic — share meaning across the app.
        default:
          "border-transparent bg-primary text-primary-foreground shadow-sm [a&]:hover:bg-primary/90",
        secondary:
          "border-transparent bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90",
        outline: "text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground",
        success:
          "border-transparent bg-success text-success-foreground shadow-sm [a&]:hover:bg-success/90",
        destructive:
          "border-transparent bg-destructive text-destructive-foreground shadow-sm [a&]:hover:bg-destructive/90",
        warning:
          "border-transparent bg-warning text-warning-foreground shadow-sm [a&]:hover:bg-warning/90",
        // Color descriptors — use only when a semantic variant doesn't fit.
        foreground: "border-transparent bg-foreground text-background shadow-sm",
        amber:
          "border-transparent bg-amber-400 text-amber-950 shadow-sm dark:bg-amber-300 dark:text-amber-950",
        slate:
          "border-transparent bg-slate-600 text-slate-50 shadow-sm dark:bg-slate-500 dark:text-slate-50",
        "amber-soft": "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400",
        "blue-soft": "border-blue-500/50 bg-blue-500/10 text-blue-700 dark:text-blue-400",
        "emerald-soft":
          "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
        "violet-soft": "border-violet-500/50 bg-violet-500/10 text-violet-700 dark:text-violet-400",
      },
      // Sizes mirror Button's size tokens so a Badge of `size="sm"` can sit
      // next to a Button of `size="sm"` with matching height.
      size: {
        xxs: "h-5 px-1.5 text-[11px] [&>svg]:size-3",
        xs: "h-6 px-2 text-xs [&>svg]:size-3",
        sm: "h-8 px-2.5 text-xs [&>svg]:size-3.5",
        default: "h-9 px-3 text-sm [&>svg]:size-4",
        lg: "h-10 px-4 text-sm [&>svg]:size-4",
      },
      font: {
        default: "",
        mono: "font-mono",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "xs",
      font: "default",
    },
  },
);

const Badge:
  & React.FC<
    & React.ComponentProps<"span">
    & VariantProps<typeof badgeVariants>
  >
  & { variants: typeof badgeVariants; } = ({
    className,
    variant,
    size,
    font,
    ...props
  }) => {
    return (
      <span
        data-slot="badge"
        className={cn(badgeVariants({ variant, size, font }), className)}
        {...props}
      />
    );
  };

Badge.variants = badgeVariants;

export { Badge, badgeVariants };
